/**
 * El recuadro del IVA en Inicio (lo pidió el dueño, 2026-10-20).
 *
 * Lo cobrado en impuestos hoy y en lo que va de mes, cada cifra con lo
 * facturado y lo mismo sin impuestos debajo; a la derecha, cada impuesto del
 * mes con su base. Tiene la forma de «Gastos», que va justo encima. El detalle
 * entero vive en Resumen › Ventas, y «Ver detalle» abre ahí, en «Este mes».
 *
 * ⚠️ Lee lo MISMO que el Resumen (`reportes.impuestos`, con la misma clave):
 * las cifras de aquí y de allá no pueden diferir.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Tarjeta, TituloSeccion } from "@/components/ui";
import { rangoDelAtajo } from "@/components/FiltroDeFechas";
import { reportes } from "@/lib/endpoints";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";
import type { ImpuestoDelPeriodo, ReporteDeImpuestos } from "@/lib/types";
import { DesgloseDeImpuestos, esSoloIva } from "@/features/reportes/Impuestos";

/**
 * «IVA», «IVA e IGTF»; con más de dos, «Impuestos». La «e» en vez de la «y»
 * delante de otra i, como se dice: «IVA e IGTF».
 */
function tituloDe(detalle: ImpuestoDelPeriodo[]): string {
  const nombres = [...new Set(detalle.map((d) => d.nombre.trim()))];
  if (nombres.length === 1) return nombres[0]!;
  if (nombres.length === 2) {
    const [a, b] = nombres as [string, string];
    return `${a} ${/^(i|hi(?![aeiouáéíóú]))/i.test(b) ? "e" : "y"} ${b}`;
  }
  return "Impuestos";
}

export function ImpuestosDeInicio({
  formato,
  impuestosActivos,
}: {
  formato: FormatoMoneda;
  /** `taxes_enabled` del negocio: sin él y sin nada cobrado en el mes, no sale. */
  impuestosActivos: boolean;
}) {
  const hoy = rangoDelAtajo("hoy");
  const mes = rangoDelAtajo("mes");
  const { data: delDia } = useQuery({
    queryKey: ["reporte-impuestos", hoy.desde, hoy.hasta],
    queryFn: () => reportes.impuestos(hoy),
  });
  const { data: delMes } = useQuery({
    queryKey: ["reporte-impuestos", mes.desde, mes.hasta],
    queryFn: () => reportes.impuestos(mes),
  });

  if (!delDia || !delMes) return null;
  if (toNumber(delMes.impuestos) === 0 && !impuestosActivos) return null;

  const soloIva = esSoloIva(delMes.detalle);
  const que = soloIva ? "IVA" : "Impuestos";

  return (
    <Tarjeta padding={false}>
      <div className="px-4 pt-4">
        <TituloSeccion
          titulo={delMes.detalle.length > 0 ? tituloDe(delMes.detalle) : "Impuestos"}
          descripcion="Lo cobrado en impuestos, con lo facturado y lo mismo sin impuestos. Ya descontadas las devoluciones."
          accion={
            <Link
              to="/reportes?ver=impuestos"
              className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Ver detalle <ChevronRight size={14} />
            </Link>
          }
        />
      </div>

      {/* Las cifras a la izquierda y cada impuesto a la derecha, como en
          «Gastos»; en el celular, uno debajo del otro. */}
      <div className="grid border-t border-line lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]">
        <div className="grid grid-cols-2 gap-4 p-4 lg:grid-cols-1 lg:border-r lg:border-line">
          <CifraDelPeriodo rotulo={`${que} de hoy`} datos={delDia} soloIva={soloIva} formato={formato} />
          <CifraDelPeriodo rotulo={`${que} del mes`} datos={delMes} soloIva={soloIva} formato={formato} />
        </div>

        <div className="p-4">
          {delMes.detalle.length === 0 ? (
            <p className="text-sm text-muted">En lo que va de mes no se ha cobrado ningún impuesto.</p>
          ) : (
            <>
              <p className="rotulo mb-2.5">Este mes, por impuesto</p>
              <DesgloseDeImpuestos data={delMes} formato={formato} />
            </>
          )}
        </div>
      </div>
    </Tarjeta>
  );
}

function CifraDelPeriodo({
  rotulo,
  datos,
  soloIva,
  formato,
}: {
  rotulo: string;
  datos: ReporteDeImpuestos;
  soloIva: boolean;
  formato: FormatoMoneda;
}) {
  return (
    <div className="min-w-0">
      <p className="rotulo truncate">{rotulo}</p>
      <p className="tabular mt-1 text-2xl font-semibold text-ink">{formatMoney(datos.impuestos, formato)}</p>
      <dl className="mt-1.5 space-y-0.5 text-xs">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted">Facturado</dt>
          <dd className="tabular text-ink-soft">{formatMoney(datos.total_facturado, formato)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted">{soloIva ? "Sin IVA" : "Sin impuestos"}</dt>
          <dd className="tabular text-ink-soft">{formatMoney(datos.sin_impuestos, formato)}</dd>
        </div>
      </dl>
    </div>
  );
}
